import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  InputBase,
  Paper,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ArrowForwardOutlined from "@mui/icons-material/ArrowForwardOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { Avatar, Chip, PillSelect, Skeleton } from "../../components/ui";
import { useThemeColors } from "../../hooks/useThemeColors";
import {
  ReviewItem,
  JobCardQueue,
  QUEUE_LABEL,
  QUEUE_STATUS,
  ReportItem,
  TaskItem,
  commentsApi,
  fetchItems,
  fetchReports,
  fetchTasks,
} from "./jobCardApi";
import { CHIP_STATUS, formatDate, money, statusLabel } from "./jobCardShared";

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "individual", label: "Individual" },
  { value: "team", label: "Team" },
];

interface Props {
  queue: JobCardQueue;
  status: string;
  onStatusChange: (status: string) => void;
  onOpen: (queue: JobCardQueue, id: number) => void;
  refreshKey: number;
}

const QueueTable = ({ queue, status, onStatusChange, onOpen, refreshKey }: Props) => {
  const colors = useThemeColors();

  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [commentCounts, setCommentCounts] = useState<Map<number, number>>(new Map());
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A different queue starts from a clean search and first page.
  useEffect(() => {
    setInput("");
    setSearch("");
    setType("");
    setPage(0);
  }, [queue]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(input.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [input]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { status, search, type, page, pageSize: rowsPerPage };
    try {
      if (queue === "task") {
        const r = await fetchTasks(params);
        setTasks(r.rows);
        setCount(r.count);
      } else if (queue === "report") {
        const r = await fetchReports(params);
        setReports(r.rows);
        setCount(r.count);
      } else {
        const r = await fetchItems(queue, params);
        setItems(r.rows);
        setCount(r.count);
      }
    } catch {
      setError("Could not load this list. Please try again.");
      setCount(0);
    } finally {
      setLoading(false);
    }
    // refreshKey reloads the list after an action in the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, status, search, type, page, rowsPerPage, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (queue === "task" || queue === "report" || items.length === 0) {
      setCommentCounts(new Map());
      return;
    }

    let cancelled = false;
    Promise.all(
      items.map(async (item) => {
        try {
          const comments = await commentsApi.list(item.kind, item.id);
          return [item.id, comments.length] as const;
        } catch {
          return null;
        }
      }),
    ).then((entries) => {
      if (!cancelled) {
        setCommentCounts(
          new Map(entries.filter((entry): entry is readonly [number, number] => entry !== null)),
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [items, queue]);

  const person = (name: string, picture?: string | null) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Avatar name={name} src={picture ?? undefined} size={30} />
      <Typography noWrap sx={{ fontSize: "0.85rem", color: colors.primary[100], maxWidth: 150 }}>
        {name}
      </Typography>
    </Box>
  );

  const cardSx = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1.75,
    p: 2,
    width: "100%",
    minWidth: 0,
    textAlign: "left",
    color: "inherit",
    border: `1px solid ${colors.primary[300]}30`,
    borderRadius: "12px",
    backgroundColor: colors.primary[900],
    cursor: "pointer",
    transition: "border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease",
    "&:hover": {
      borderColor: `${colors.primary[300]}80`,
      transform: "translateY(-2px)",
      boxShadow: `0 8px 24px ${colors.primary[300]}12`,
    },
    "&:focus-visible": {
      outline: `2px solid ${colors.primary[300]}`,
      outlineOffset: 2,
    },
  } as const;

  const cardAction = (label: string) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 1.25,
        mt: "auto",
        borderTop: `1px solid ${colors.primary[300]}20`,
        color: colors.primary[100],
      }}
    >
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>{label}</Typography>
      <ArrowForwardOutlined sx={{ fontSize: 18 }} />
    </Box>
  );

  const detail = (label: string, value: string) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.7rem", color: colors.primary[300] }}>{label}</Typography>
      <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.primary[100] }}>
        {value}
      </Typography>
    </Box>
  );

  const cards =
    queue === "task"
      ? tasks.map((task) => (
          <Box
            key={task.id}
            component="button"
            type="button"
            onClick={() => onOpen("task", task.id)}
            sx={cardSx}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              {person(task.internName)}
              <Chip status={CHIP_STATUS[task.status] ?? "neutral"} label={statusLabel(queue, task.status)} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.72rem", color: colors.primary[300], mb: 0.4 }}>
                {task.taskId}
              </Typography>
              <Typography sx={{ fontSize: "0.98rem", fontWeight: 700, color: colors.primary[100] }}>
                {task.title}
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {detail("Work date", formatDate(task.workDate))}
              {detail("Hours logged", String(task.hoursLogged))}
            </Box>
            {cardAction(task.status === "pending" ? "Review task" : "View task")}
          </Box>
        ))
      : queue === "report"
        ? reports.map((report) => (
            <Box
              key={report.id}
              component="button"
              type="button"
              onClick={() => onOpen("report", report.id)}
              sx={cardSx}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Chip status={CHIP_STATUS[report.status] ?? "neutral"} label={statusLabel(queue, report.status)} />
                <Typography sx={{ fontSize: "0.75rem", color: colors.primary[300], textTransform: "capitalize" }}>
                  {report.type} report
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.98rem", fontWeight: 700, color: colors.primary[100] }}>
                  {report.name}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: colors.primary[300], mt: 0.5 }}>
                  {report.periodLabel}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {report.metrics.slice(0, 2).map((metric) => detail(metric.label, metric.value))}
              </Box>
              {cardAction(report.status === "draft" ? "Review report" : "View report")}
            </Box>
          ))
        : items.map((item) => {
            const action =
              item.status === "pending" && item.kind === "proposal"
                ? "Review proposal"
                : item.status === "completed" && item.kind === "assignment"
                  ? "Approve assignment"
                  : `View ${item.kind}`;
            return (
              <Box
                key={item.key}
                component="button"
                type="button"
                onClick={() => onOpen(queue, item.id)}
                sx={cardSx}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    {person(item.internName, item.internPicture)}
                    {!!commentCounts.get(item.id) && (
                      <Tooltip title={`${commentCounts.get(item.id)} comment${commentCounts.get(item.id) === 1 ? "" : "s"}`}>
                        <Box
                          aria-label={`${commentCounts.get(item.id)} comments`}
                          sx={{ display: "flex", alignItems: "center", gap: 0.35, color: colors.primary[300], flexShrink: 0 }}
                        >
                          <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                            {commentCounts.get(item.id)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <Chip status={CHIP_STATUS[item.status] ?? "neutral"} label={statusLabel(queue, item.status)} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.98rem", fontWeight: 700, color: colors.primary[100] }}>
                    {item.title}
                  </Typography>
                  {item.description && (
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color: colors.primary[300],
                        mt: 0.6,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  {detail(item.kind === "proposal" ? "Deadline" : "Due date", formatDate(item.dueDate))}
                  {detail(item.hours !== null ? "Estimated payout" : "Reward", money(item.reward))}
                  {item.hours !== null && detail("Estimated hours", String(item.hours))}
                </Box>
                {cardAction(action)}
              </Box>
            );
          });

  return (
    <Paper
      elevation={0}
      sx={{ backgroundColor: colors.primary[900], borderRadius: "12px", padding: "20px" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "background.paper",
            px: 1,
            borderRadius: "4px",
          }}
        >
          <InputBase
            placeholder={`Search ${QUEUE_LABEL[queue].toLowerCase()}...`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{ flex: 1, fontSize: "14px", minWidth: { xs: 180, sm: 300 } }}
          />
          <IconButton size="small" onClick={() => setSearch(input.trim())}>
            <SearchOutlined sx={{ fontSize: "20px", color: "text.secondary" }} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {queue === "report" && (
            <Box sx={{ width: 150 }}>
              <PillSelect
                value={type}
                onChange={(e) => {
                  setType(String(e.target.value));
                  setPage(0);
                }}
                displayEmpty
                options={TYPE_OPTIONS}
              />
            </Box>
          )}
          <Box sx={{ width: 190 }}>
            <PillSelect
              value={status}
              onChange={(e) => {
                onStatusChange(String(e.target.value));
                setPage(0);
              }}
              displayEmpty
              options={[{ value: "", label: "All statuses" }, ...QUEUE_STATUS[queue]]}
            />
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: colors.primary[100], mb: 1 }}>
          {queue === "proposal" && "Review proposals"}
          {queue === "assignment" && "Review assignments"}
          {queue === "task" && "Task queue"}
          {queue === "report" && "Report queue"}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: colors.primary[300] }}>
          {queue === "proposal" && "Approved proposals create assignments, so the next action is to review the work before it is paid."}
          {queue === "assignment" && "Completed work is waiting for approval before a payout can be generated."}
          {queue === "task" && "Tasks need review before hours are approved and paid."}
          {queue === "report" && "Reports and summaries are reviewed for accuracy before they are finalised."}
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: 1.5,
          }}
        >
          {Array.from({ length: Math.min(rowsPerPage, 6) }).map((_, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 2,
                minHeight: 180,
                borderRadius: "12px",
                border: `1px solid ${colors.primary[300]}20`,
                backgroundColor: colors.primary[900],
              }}
            >
              <Skeleton variant="text" width="45%" height={28} />
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="65%" height={24} />
              <Skeleton variant="text" width="35%" height={24} sx={{ mt: 2 }} />
            </Paper>
          ))}
        </Box>
      ) : count === 0 && !error ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: colors.primary[300], fontSize: "0.9rem" }}>
            Nothing here for this filter.
          </Typography>
          {status && (
            <Typography
              component="button"
              onClick={() => onStatusChange("")}
              sx={{
                mt: 1,
                border: 0,
                background: "transparent",
                color: colors.primary[100],
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Show all statuses
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: 1.5,
          }}
        >
          {cards}
        </Box>
      )}

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: "1px solid #E0E0E0",
          "& .MuiTablePagination-toolbar": { justifyContent: "center" },
        }}
      />
    </Paper>
  );
};

export default QueueTable;
